import { useMutation } from '@tanstack/react-query';
import { AxiosError, isAxiosError } from 'axios';
import apiClient from '../libs/apiClient';
import { ApiResponse } from '../types/response';
import { SessionCmxUrl, SessionGetCMXUrlParams } from '../types/session';

const createUploadAction = async ({ sessionId, document }: SessionGetCMXUrlParams) => {
  const {
    data: { data: cmxData },
  } = await apiClient.post<ApiResponse<SessionCmxUrl>>(`session/${sessionId}/documents/${document.documentId}/action`, {
    type: 'UPLOAD_STARTED',
  });

  try {
    await Promise.all(
      document.files.map((file) => {
        const form = new FormData();
        form.append('file', file.content as File);
        return apiClient.post(cmxData.cmxUploadUrl, form, {
          headers: cmxData.headers,
        });
      }),
    );
    const { status } = await apiClient.post(`session/${sessionId}/documents/${document.documentId}/action`, {
      type: 'UPLOAD_COMPLETED',
      cmxDocumentId: cmxData.cmxDocumentId,
    });
    return { status, documentId: document.documentId };
  } catch (error) {
    let errorObj;
    if (isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      errorObj = {
        type: 'AxiosError',
        message: axiosErr.message,
        statusCode: axiosErr.response?.status,
        errorCode: axiosErr.code,
        stack: axiosErr.stack,
        name: axiosErr.name,
      };
    } else if (error instanceof Error) {
      errorObj = {
        type: 'Error',
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    } else {
      errorObj = {
        type: 'StringError',
        message: String(error),
      };
    }
    const { status } = await apiClient.post(`session/${sessionId}/documents/${document.documentId}/action`, {
      type: 'UPLOAD_FAILED',
      cmxDocumentId: cmxData.cmxDocumentId,
      error: errorObj,
    });
    return { error: errorObj, status, document: document };
  }
};

const useCreateUploadAction = () => {
  return useMutation({
    mutationFn: createUploadAction,
  });
};

export default useCreateUploadAction;
